import { useState, useRef, useEffect, useContext } from 'react'
import ReactDOM from 'react-dom'
import classnames from 'classnames/bind'
import dynamic from 'next/dynamic'
import { isEmpty } from 'lodash-es'
import { BigNumber } from 'bignumber.js'
import { useTranslation } from 'next-i18next'
import { useMutation } from 'react-query'

// Libs
import { useAppSelector } from '@/redux/hooks'
import useMedia from '@/hooks/useMedia'
import useFullScreen from '@/hooks/useFullScreen'
import { useIsCSR } from '@/hooks/useIsCSR'
import { pxecPostMessages } from '@/utils/pxec/postMessages'
import { increaseShares } from '@/api/increaseShares'

// Components
import Share from '../../sharedComponents/Share'
import Merchant from '../../sharedComponents/Merchant'
import MerchantInfo from '../../sharedComponents/MerchantInfo'
import Like from '../../sharedComponents/Like'
import MicRequest from '../../sharedComponents/MicRequest'
import ProductList from '../../sharedComponents/ProductList'
import MessageList from '../../sharedComponents/MessageList'
import ChatInput from '../../sharedComponents/ChatInput'
import TabItem from './components/TabItem'
import HandUpList from '../../sharedComponents/HandUpList'
import PhoneAction from '../../sharedComponents/PhoneAction'
import PrepareAction from '../../sharedComponents/PrepareAction'
import Image from '@/components/common/Image'
import RoomCover from '../../sharedComponents/RoomCover'
import Playback from '../../sharedComponents/Playback'
import NetworkStatus from '../../sharedComponents/NetworkStatus'
import Icon from '@/assets/icons'
import SharePopup from '@/components/shared/SharePopup'
import Popup from '@/components/common/Popup'
import Drawer from '@/components/common/Drawer'
import Notification from '../../sharedComponents/Notification'
import Bag from '../../sharedComponents/Bag'
import PortalWithPlayer from '@/components/common/PortalWithPlayer'
import ActionIcon from '@/components/common/ActionIcon'
import More from './components/More'

// Constants
import { CONNECTION_STATUS } from '@/constants/socketEvent'
import { GtmShareContentType } from '@/constants/gtmEvent'
import { VALID_BRAND_NAME } from '@/constants/brand'

// Contexts
import { LiveRoomContext } from '@/components/pages/LiveRoom/index'

// Types
import { RoomProps } from '@/types/room'

// Styles
import styles from './style.module.scss'

const Player = dynamic(() => import('@/components/common/Player'), {
  ssr: false,
})
const TickerTape = dynamic(() => import('@/components/pages/LiveRoom/sharedComponents/TickerTape'), {
  ssr: false,
})

const cx = classnames.bind(styles)

const TAB = {
  CHAT: 'chat',
  MIC: 'mic',
}

const Desktop = (props: RoomProps) => {
  const {
    srcWithQuality,
    merchantData,
    tickerTape,
    productList,
    roomPlacard,
    likes,
    isWebRTC,
    role,
    isHost = false,
    handleMicRequest,
    handleMicWaitingClick,
    isWaitingConnect,
    isMicConnecting,
    startTime = '',
    liveTime = '',
    liveCloseTime = '',
    endTime = '',
    actionData,
    isLive,
    isPast,
    isFuture,
    isShowPlayer,
    roomCoverData,
    isChatDisabled,
    hasPlayback,
    playbackData,
    liveType,
    isBySelf,
    setIsBySelf,
    isLessThenOneHour,
    handUpUserList,
  } = props

  const { t } = useTranslation()

  const liveRoomContext: any = useContext(LiveRoomContext)
  const { brandName } = liveRoomContext

  const [currentTab, setCurrentTab] = useState(TAB.CHAT)
  const [sharePopup, setSharePopup] = useState(false)
  const [isProductListDrawerOpen, setIsProductListDrawerOpen] = useState(false)
  const [isLargeText, setIsLargeText] = useState(false)
  const [isDidMount, setIsDidMount] = useState(false)
  const [currentBagOffsetX, setCurrentBagOffsetX] = useState(0)
  const [currentVideoWidth, setCurrentVideoWidth] = useState(0)

  const videoRef: any = useRef(null)
  const chatAreaRef = useRef(null)
  const micListPopopRef = useRef<any>(null)
  const morePopopRef = useRef<any>(null)
  const roomRef = useRef(null)

  const { isFullScreen } = useFullScreen()
  const { isCSR } = useIsCSR()

  const { roomId, memberInfoId } = useAppSelector((state) => state.room?.roomData)

  const socketData = useAppSelector((state) => state.socket)

  const {
    socket,
    messageList,
    notificationList,
    canHandUp,
    handUpList,
    micOnList,
    participantList,
    stateData,
    connectStatus,
    userToken,
    userCount,
    silentUserList,
  } = socketData

  const isWebRTCConnecting = connectStatus === CONNECTION_STATUS.CONNECTING

  const { likes: likesBySocket, views: viewsBySocket } = stateData

  const { isDesktopMiddle, isMobile } = useMedia()

  const productAmount = productList ? productList?.length : 0

  const messageListOnlyChat = messageList.filter((item) => item.type === 'chat')

  const actionWrapperContainer: any = typeof window === 'undefined' ? null : document.getElementById('actionWrapper')
  const phoneActionWrapperContainer: any = typeof window === 'undefined' ? null : document.getElementById('phoneActionWrapper')

  const hasContainer = actionWrapperContainer || phoneActionWrapperContainer

  const { mutate: increaseSharesMutation } = useMutation(increaseShares)

  useEffect(() => {
    if (isDidMount && videoRef.current) {
      const handleBagOffset = () => {
        const width = window.innerWidth
        const height = window.innerHeight

        const videoWidth = videoRef?.current?.videoWidth
        const videoHeight = videoRef?.current?.videoHeight

        const widthWithRatio = (videoWidth * height) / videoHeight
        const heightWithRatio = (width - widthWithRatio) / 2
        const bagOffsetX = width - (widthWithRatio + heightWithRatio) + 12

        setCurrentVideoWidth(widthWithRatio)
        setCurrentBagOffsetX(bagOffsetX)
      }

      handleBagOffset()

      window.addEventListener('resize', handleBagOffset)

      return () => {
        window.removeEventListener('resize', handleBagOffset)
      }
    }
  }, [isDidMount, videoRef.current, isFullScreen])

  useEffect(() => {
    setIsDidMount(true)
  }, [])

  useEffect(() => {
    if (isPast && currentTab !== TAB.CHAT) {
      setCurrentTab(TAB.CHAT)
    }
  }, [isPast, currentTab])

  return (
    <div className={cx('room')} ref={roomRef}>
      {isHost && (
        <MerchantInfo
          {...merchantData}
          dummyViews={viewsBySocket > 0 ? viewsBySocket : merchantData?.dummyViews}
          startTime={startTime}
          liveTime={liveTime}
          liveCloseTime={liveCloseTime}
          endTime={endTime}
          userCount={userCount}
          isFuture={isFuture}
          isPast={isPast}
          liveType={liveType}
        />
      )}

      <div className={cx('room-background')}>
        <Image src='/images/liveroom-background@2x.png' fill alt='live room background' style={{ objectFit: 'cover' }} sizes='400px' />
      </div>

      <div className={cx('room-wrapper', { isHost })}>
        <Share className={cx('room-share')} socket={socket} />

        <div className={cx('room-content')}>
          <div id='room' className={cx('room-content-live')}>
            {isShowPlayer && !hasPlayback && (
              <Player videoRef={videoRef} src={srcWithQuality} isWebRTC={isWebRTC} role={role} isWebRTCConnecting={isWebRTCConnecting} />
            )}

            {!isLive && !isShowPlayer && !hasPlayback && <RoomCover {...roomCoverData} />}

            {hasPlayback && <Playback {...playbackData} />}

            <div className={cx('room-content-live-content')}>
              {isLive && (
                <>
                  {(isHost || isMicConnecting) && (
                    <PhoneAction
                      isHost={isHost}
                      userToken={userToken}
                      role={role}
                      isMicConnecting={isMicConnecting}
                      isWebRTCConnecting={isWebRTCConnecting}
                      canHandUp={canHandUp}
                      socket={socket}
                      {...actionData}
                    />
                  )}
                </>
              )}
              {isCSR &&
                !isWebRTC &&
                hasContainer &&
                ReactDOM.createPortal(
                  <div className={cx('room-content-live-content-more')} onClick={() => morePopopRef.current.setIsPopupOpen(true)}>
                    <ActionIcon icon={Icon.More} />
                  </div>,

                  (isHost
                    ? phoneActionWrapperContainer
                    : isWebRTCConnecting
                    ? phoneActionWrapperContainer
                    : actionWrapperContainer || document.body) || document.body,
                )}
              {isFuture && isShowPlayer && <PrepareAction {...actionData} />}
              <div className={cx('room-content-live-content-header')}>
                <div className={cx('room-content-live-content-header-row')}>
                  <Merchant {...merchantData} dummyViews={viewsBySocket > 0 ? viewsBySocket : merchantData?.dummyViews} />

                  {isWebRTC && <NetworkStatus className={cx('room-content-network')} />}
                </div>

                {!isEmpty(tickerTape) && <TickerTape tickerTape={tickerTape} />}
              </div>
              <div className={cx('room-content-row')}>
                <div className={cx('room-content-wrapper', { isPast })}>
                  {isDesktopMiddle && <Notification className={cx('room-content-notification')} notificationList={notificationList} />}
                  {(isLive || !isEmpty(messageListOnlyChat)) && (
                    <MessageList
                      socket={socket}
                      messageList={messageList.filter((item) => item.type === 'chat' || item.type === 'placard')}
                      roomPlacard={roomPlacard}
                      isBySelf={isBySelf}
                      setIsBySelf={setIsBySelf}
                      isHost={isHost}
                      silentUserList={silentUserList}
                    />
                  )}
                </div>

                {isDesktopMiddle && canHandUp && !isHost && (
                  <MicRequest
                    className={cx('room-content-live-content-action-mic')}
                    handleMicRequest={handleMicRequest}
                    handleMicWaitingClick={handleMicWaitingClick}
                    isWaitingConnect={isWaitingConnect}
                    isMicConnecting={isMicConnecting}
                  />
                )}
              </div>
              <div id='roomAction' className={cx('room-content-live-content-action', 'no-select')}>
                {isDesktopMiddle && (
                  <ChatInput
                    className={cx('room-content-live-content-action-input')}
                    isChatDisabled={isChatDisabled}
                    setIsBySelf={setIsBySelf}
                    isHost={isHost}
                    hostId={memberInfoId}
                    roomId={roomId}
                  />
                )}

                {isDesktopMiddle && isHost && isLive && (
                  <div className={cx('room-content-live-content-action-icon')} onClick={() => micListPopopRef.current.setIsPopupOpen(true)}>
                    <Icon.MicConnect />

                    {handUpUserList?.length > 0 && (
                      <div className={cx('room-content-live-content-action-icon-mic-count')}>
                        <span className={cx('room-content-live-content-action-icon-mic-count-number')}>
                          {new BigNumber(handUpUserList?.length).toFormat()}
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {!isDesktopMiddle && canHandUp && !isHost && (
                  <MicRequest
                    className={cx('room-content-live-content-action-mic')}
                    handleMicRequest={handleMicRequest}
                    handleMicWaitingClick={handleMicWaitingClick}
                    isWaitingConnect={isWaitingConnect}
                    isMicConnecting={isMicConnecting}
                  />
                )}

                <div
                  className={cx('room-content-live-content-action-share')}
                  onClick={() => {
                    if (brandName === VALID_BRAND_NAME.PXEC && roomId) {
                      pxecPostMessages.shareRoom(roomId)
                      increaseSharesMutation({
                        roomId,
                      })
                      return
                    }
                    setSharePopup(true)
                  }}
                >
                  <Icon.RoomShare />
                </div>

                <Like className={cx('room-content-live-content-action-like')} likes={likesBySocket > 0 ? likesBySocket : likes} />
              </div>
            </div>
          </div>
          {!isDesktopMiddle && (
            <div className={cx('room-tab')}>
              {/* 直播主才會有切換連麥的頁面 */}
              {isHost && isLive && (
                <div className={cx('room-tab-wrapper')}>
                  <TabItem
                    className={cx('room-tab-item')}
                    label={t('LiveRoom_Tab_Chat')}
                    isActive={currentTab === TAB.CHAT}
                    handleItemClick={() => setCurrentTab(TAB.CHAT)}
                  />
                  <TabItem
                    className={cx('room-tab-item')}
                    label={t('LiveRoom_Tab_Mic')}
                    isActive={currentTab === TAB.MIC}
                    handleItemClick={() => setCurrentTab(TAB.MIC)}
                    count={handUpUserList?.length}
                  />
                </div>
              )}

              <div className={cx('room-tab-content')}>
                {currentTab === TAB.CHAT && (
                  <div ref={chatAreaRef} className={cx('room-tab-content-chat')}>
                    <div className={cx('room-tab-content-chat-list-wrapper')}>
                      <MessageList
                        className={cx('room-tab-content-chat-list')}
                        socket={socket}
                        messageList={messageList}
                        roomPlacard={roomPlacard}
                        isBySelf={isBySelf}
                        setIsBySelf={setIsBySelf}
                        isLarge={isLargeText}
                        isHost={isHost}
                        silentUserList={silentUserList}
                      />
                    </div>

                    <div className={cx('room-tab-content-chat-input')}>
                      {isHost && (
                        <div
                          className={cx('room-tab-content-chat-input-icon', { isActive: isLargeText })}
                          onClick={() => setIsLargeText((prev) => !prev)}
                        >
                          <Icon.Text />
                        </div>
                      )}
                      <ChatInput isChatDisabled={isChatDisabled} setIsBySelf={setIsBySelf} isHost={isHost} hostId={memberInfoId} roomId={roomId} />
                    </div>
                  </div>
                )}

                {currentTab === TAB.MIC && (
                  <div className={cx('room-tab-content-mic')}>
                    <HandUpList micOnList={micOnList} handUpList={handUpList} canHandUp={canHandUp} participantList={participantList} />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <ProductList
          className={cx('room-list')}
          productList={productList}
          isLive={isLive}
          isHost={isHost}
          isFuture={isFuture}
          isLessThenOneHour={isLessThenOneHour}
        />
      </div>
      {isFullScreen && (
        <PortalWithPlayer isFullScreen={isFullScreen}>
          <Bag
            className={cx({ 'room-player-bag': isFullScreen })}
            style={{ right: currentBagOffsetX }}
            hanldeBagClick={() => setIsProductListDrawerOpen(true)}
            count={productAmount}
          />
        </PortalWithPlayer>
      )}

      <PortalWithPlayer isFullScreen={isFullScreen}>
        <Drawer
          className={cx('room-drawer')}
          open={isProductListDrawerOpen && isFullScreen}
          direction='bottom'
          onClose={() => setIsProductListDrawerOpen(false)}
          lockBackgroundScroll
          style={{ width: currentVideoWidth, margin: '0px auto' }}
        >
          <ProductList
            className={cx('room-list', { isFullScreen })}
            productList={productList}
            isLive={isLive}
            isHost={isHost}
            isLessThenOneHour={isLessThenOneHour}
          />
        </Drawer>
      </PortalWithPlayer>

      <Popup ref={micListPopopRef} hasConfirm={false} hasCancel={false} isDrawer>
        <div className={cx('room-mic-list')}>
          <p className={cx('room-mic-list-title')}>{t('LiveRoom_MicListPopup_Title')}</p>
          <HandUpList micOnList={micOnList} handUpList={handUpList} canHandUp={canHandUp} participantList={participantList} />
        </div>
      </Popup>

      {/* 更多功能 */}
      {!isMobile && (
        <Popup ref={morePopopRef} hasConfirm={false} hasCancel={false}>
          <More isHost={isHost} morePopopRef={morePopopRef} />
        </Popup>
      )}

      <SharePopup isOpen={sharePopup} onCancel={() => setSharePopup(false)} gtmShareContentType={GtmShareContentType.CONTENT_TYPE_LIVEROOM} />
    </div>
  )
}

export default Desktop
